/**
 * OncoDiary PATIENT Quiz Flow
 * For cancer survivors/patients
 *
 * URL: /quiz/patient
 * 23 Pages - tailored for patient journey
 */

export const patientQuizData = {
  metadata: {
    audience: "patient",
    totalPages: 31,
    title: "OncoDiary Support Quiz - For Cancer Survivors",
    description: "Discover the right OncoDiary resources for your recovery",
    timeToComplete: "3-4 minutes"
  },

  // PAGE 0: HOME PAGE (Patient-specific)
  home: {
    pageNum: 0,
    type: "informational",
    headline: "Your recovery matters. Both your heart and your body.",
    subheading: "After cancer treatment, anxiety, sleepless nights, and that feeling of being overwhelmed—these are real. And you're not meant to carry them alone.",
    benefits: [
      "Break through isolation and feel truly understood",
      "Learn practical tools designed for cancer recovery",
      "Sleep better and worry less",
      "Rebuild your confidence and reclaim your life"
    ],
    cta: "Let's find the right support for you",
    ctaText: "Continue",
    nextPage: "page1_gender"
  },

  // PAGE 1: GENDER (First Question)
  page1_gender: {
    pageNum: 1,
    type: "question",
    singleSelect: true,
    question: "How do you identify?",
    emoji: "👤",
    options: [
      { label: "👩 Female", value: "female" },
      { label: "👨 Male", value: "male" },
      { label: "🌈 Non-binary / Prefer not to say", value: "other" }
    ],
    nextPage: "page1_age"
  },

  // PAGE 1.5: AGE (New)
  page1_age: {
    pageNum: 2,
    type: "question",
    singleSelect: true,
    question: "How old are you?",
    emoji: "🤔",
    options: [
      { label: "18-29", value: "18-29" },
      { label: "30-39", value: "30-39" },
      { label: "40-49", value: "40-49" },
      { label: "50-59", value: "50-59" },
      { label: "60-69", value: "60-69" },
      { label: "70+", value: "70+" }
    ],
    nextPage: "page1_treatment_stage"
  },

  // PAGE 2: TREATMENT STAGE (Patient-specific)
  page1_treatment_stage: {
    pageNum: 3,
    type: "question",
    singleSelect: true,
    question: "Where are you in your cancer journey?",
    emoji: "🏥",
    subtext: "This helps us tailor recommendations to where you are right now",
    options: [
      { label: "🩺 Still in active treatment", value: "active_treatment" },
      { label: "✅ Just finished treatment (0-6 months ago)", value: "recent_finish" },
      { label: "📅 1-2 years post-treatment", value: "1-2_years" },
      { label: "⏳ 3+ years post-treatment", value: "3plus_years" }
    ],
    nextPage: "page2_social_proof"
  },

  // PAGE 2: SOCIAL PROOF (Patient-specific, gender-aware)
  page2_social_proof: {
    pageNum: 3,
    type: "informational",
    singleSelect: false,
    heading: "You're part of a growing community of survivors",
    contentByGender: {
      female: {
        message: "Your heart and mind deserve as much care as your body did during treatment. Many women survivors discover that healing emotionally is where the real transformation happens.\n\nYou're part of a growing community. Designed by psycho-oncologist Ellie Urban, grounded in research, and trusted by thousands of survivors like you.",
        image: "/images/placeholder-women-survivors.webp"
      },
      male: {
        message: "Your heart and mind deserve as much care as your body did during treatment. Many men survivors discover that healing emotionally is where the real transformation happens.\n\nYou're part of a growing community. Designed by psycho-oncologist Ellie Urban, grounded in research, and trusted by thousands of survivors like you.",
        image: "/images/placeholder-men-survivors.webp"
      }
    },
    nextPage: "page3_primary_concern"
  },

  // PAGE 3: PRIMARY CONCERN (Patient-specific)
  page3_primary_concern: {
    pageNum: 4,
    type: "question",
    singleSelect: false,
    question: "What's your biggest struggle right now?",
    subtext: "Choose all that apply",
    options: [
      { label: "😰 Anxiety and fear (about recurrence or health)", value: "anxiety", emoji: "😰" },
      { label: "😴 Insomnia or disrupted sleep", value: "insomnia", emoji: "😴" },
      { label: "😞 Depression or emotional numbness", value: "depression", emoji: "😞" },
      { label: "😤 Frustration with my body or identity changes", value: "identity", emoji: "😤" },
      { label: "😕 Feeling isolated or disconnected from others", value: "isolation", emoji: "😕" },
      { label: "🧠 Brain fog or trouble concentrating", value: "brain_fog", emoji: "🧠" },
      { label: "😫 Grief, loss, or processing the journey", value: "grief", emoji: "😫" }
    ],
    nextPage: "page4_physical_recovery"
  },

  // PAGE 4: PHYSICAL RECOVERY (Patient-specific)
  page4_physical_recovery: {
    pageNum: 5,
    type: "question",
    singleSelect: false,
    question: "How is the physical side affecting your recovery?",
    subtext: "Choose all that apply",
    options: [
      { label: "🫧 Side effects lingering or new symptoms appearing", value: "side_effects" },
      { label: "💪 Weakness or loss of physical strength", value: "weakness" },
      { label: "⚡ Fatigue that won't go away", value: "fatigue" },
      { label: "🏃 Can't return to exercise or physical activities", value: "no_activity" },
      { label: "👶 Concerns about fertility or having children", value: "fertility" },
      { label: "💕 Sexual dysfunction or intimacy issues", value: "intimacy" },
      { label: "👍 Physically doing okay, but emotionally struggling", value: "emotional_focus" }
    ],
    nextPage: "page5_treatment_duration"
  },

  // PAGE 5: TIME SINCE TREATMENT (Patient-specific)
  page5_treatment_duration: {
    pageNum: 6,
    type: "question",
    singleSelect: true,
    question: "How long have you been dealing with these emotions?",
    emoji: "⏱️",
    options: [
      { label: "📍 Since I was diagnosed", value: "since_diagnosis" },
      { label: "🏥 Throughout treatment", value: "during_treatment" },
      { label: "🎉 Since treatment ended", value: "after_treatment" },
      { label: "📉 It's gotten worse over time", value: "worsening" }
    ],
    nextPage: "page6_severity"
  },

  // PAGE 6: SEVERITY (Patient-specific)
  page6_severity: {
    pageNum: 7,
    type: "question",
    singleSelect: true,
    question: "On a scale, how would you describe what you're experiencing?",
    emoji: "📊",
    options: [
      { label: "🚨 I'm in crisis — I need help now", value: "crisis" },
      { label: "⚠️ Severe — it's consuming my life", value: "severe" },
      { label: "🟡 Moderate — it's affecting everything", value: "moderate" },
      { label: "🟢 Mild — it's noticeable but manageable", value: "mild" }
    ],
    nextPage: "page6_self_care_tools"
  },

  // PAGE 6.5: SELF-CARE TOOLS & COPING STRATEGIES (Patient-specific)
  page6_self_care_tools: {
    pageNum: 8,
    type: "question",
    singleSelect: false,
    question: "What tools or practices are you currently using for self-care or emotional support?",
    emoji: "🌱",
    subtext: "Choose all that apply",
    options: [
      { label: "🧘 Meditation or mindfulness", value: "meditation" },
      { label: "📝 Journaling or writing", value: "journaling" },
      { label: "💬 Talking to friends/family", value: "talking_friends_family" },
      { label: "🛋️ Therapy or counseling", value: "therapy" },
      { label: "👥 Support groups", value: "support_groups" },
      { label: "🏃 Movement (yoga, walking, exercise)", value: "movement" },
      { label: "🌬️ Breathing exercises", value: "breathing" },
      { label: "📚 Reading/learning about cancer", value: "reading_learning" },
      { label: "🙏 Spiritual or faith practices", value: "spiritual_faith" },
      { label: "❌ I'm not using any tools right now", value: "none" },
      { label: "❓ Other", value: "other" }
    ],
    nextPage: "page7_holistic_sleep"
  },

  // PAGE 7: HOLISTIC ASSESSMENT — SLEEP
  page7_holistic_sleep: {
    pageNum: 9,
    type: "question",
    singleSelect: true,
    question: "How's your sleep right now?",
    subtext: "Sleep quality directly impacts emotional recovery. Research shows poor sleep delays healing by 40%.",
    options: [
      { label: "😴 I sleep 7-9 hours and feel rested", value: "good_sleep" },
      { label: "😴 I sleep 5-7 hours but feel tired", value: "moderate_sleep" },
      { label: "😵 I get less than 5 hours", value: "poor_sleep" },
      { label: "🌙 My sleep is completely disrupted", value: "no_sleep" }
    ],
    nextPage: "page8_holistic_movement",
    research: "Sleep Foundation, National Institutes of Health (2023)"
  },

  // PAGE 8: HOLISTIC ASSESSMENT — MOVEMENT
  page8_holistic_movement: {
    pageNum: 10,
    type: "question",
    singleSelect: true,
    question: "How much are you moving right now?",
    subtext: "Physical movement is proven to reduce anxiety by 35% and improves emotional regulation.",
    options: [
      { label: "🏃 Regular exercise/movement (walks, yoga, etc.)", value: "active" },
      { label: "🚶 Light daily activity (housework, errands)", value: "light_activity" },
      { label: "🪑 Mostly sitting/resting", value: "sedentary" },
      { label: "😫 I'm too fatigued to move much", value: "no_movement" }
    ],
    nextPage: "page9_holistic_nutrition",
    research: "American Psychological Association, Journal of Clinical Psychology (2022)"
  },

  // PAGE 9: HOLISTIC ASSESSMENT — NUTRITION
  page9_holistic_nutrition: {
    pageNum: 11,
    type: "question",
    singleSelect: true,
    question: "How's your eating and drinking?",
    subtext: "Proper hydration and nutrition stabilize energy and mood. Dehydration increases anxiety by 30%.",
    options: [
      { label: "🍽️ Regular meals, staying hydrated", value: "good_nutrition" },
      { label: "⏭️ Skip meals, forget to drink water", value: "inconsistent" },
      { label: "🤢 Nausea or appetite changes", value: "nausea" },
      { label: "😰 Food causes anxiety or pain", value: "food_anxiety" }
    ],
    nextPage: "page10_holistic_stress",
    research: "Nutrition Review, Harvard Medical School (2023)"
  },

  // PAGE 10: HOLISTIC ASSESSMENT — STRESS & ROUTINE
  page10_holistic_stress: {
    pageNum: 12,
    type: "question",
    singleSelect: true,
    question: "What helps you manage stress and maintain routine?",
    subtext: "Survivors with consistent routines and stress management practices recover 2x faster than those without.",
    options: [
      { label: "📅 Structured routine and consistent stress management", value: "good_routine" },
      { label: "🕐 Some routine, trying to manage stress", value: "partial_routine" },
      { label: "🌀 No routine, everything feels scattered", value: "no_routine" },
      { label: "😴 Want routine but too exhausted", value: "exhausted" }
    ],
    nextPage: "page11_holistic_integration",
    research: "Cancer Survivorship Research, Mayo Clinic (2023)"
  },

  // PAGE 11: HOW IT ALL CONNECTS — HOLISTIC RECOVERY
  page11_holistic_integration: {
    pageNum: 13,
    type: "informational",
    heading: "Recovery isn't just emotional—it's physical too",
    image: "/images/placeholder-how-it-works.webp",
    message: `You just told me about four foundations of your recovery: sleep, movement, nutrition, and routine.

Here's what research shows: **these aren't separate things. They work together.**

When you **sleep better**, your nervous system calms down. Anxiety drops. You have energy to move.

When you **move your body**—even just walking—your brain releases the chemicals that fight depression. Your appetite regulates. You sleep deeper.

When you **eat and drink consistently**, your energy stabilizes. Your mood improves. You have strength to handle stress.

When you **build routine and manage stress**, your body knows what to expect. Your nervous system relaxes. Sleep comes easier. Everything becomes possible.

**They're all connected. Each one makes the others work better.**

This is why so many survivors feel stuck on emotion alone: you can talk about your feelings for months, but if you're not sleeping, moving, eating, and building stability, your nervous system stays in survival mode.

**Your recovery needs BOTH:**
• Emotional support (processing trauma, community, reflection)
• Physical wellness (sleep tracking, movement, nutrition, routine building)

The app you're about to explore tracks both. Because real recovery happens when you address both.

You're not broken. Your nervous system just needs tools to remember that the danger is over.`,
    ctaText: "Continue",
    nextPage: "page7_educational_insight_1"
  },

  // PAGE 7: EDUCATIONAL INSIGHT — THE CANCER PARADOX (1 of 3)
  page7_educational_insight_1: {
    pageNum: 14,
    type: "informational",
    singleSelect: false,
    heading: "Why the pain doesn't end when treatment does",
    image: "/images/placeholder-brain-nervous-system.webp",
    content: `
You rang the bell. Treatment is over. You should feel relieved.

But instead, you feel terrified.

During treatment, your nervous system learned one job: stay alert. Watch for danger. Prepare for the worst. Your brain flooded with stress hormones — adrenaline, cortisol — every single day. This wasn't anxiety you could control. It was survival.

**That training doesn't switch off when treatment ends.**

Your body doesn't know the crisis is over. Because in many ways, for you, it isn't.

You still panic at a new ache. You still hold your breath during follow-up appointments. You still wake up at 3 AM with your heart racing, convinced something's wrong.

**This isn't weakness. This is your nervous system doing exactly what it was trained to do.**

During cancer, your mind learned: *Your body is a threat. Danger is real.*

The medical system treated the cancer. But no one treated the nervous system that's still in survival mode.

Without tools specifically designed to help your nervous system reset, survivors can stay stuck in this state for months or years. The longer you stay there, the more your brain gets used to feeling afraid.

Recovery means learning to rewire that response — not through willpower, but through **trained guidance that understands this specific trauma**.
    `,
    nextPage: "page8_educational_insight_2"
  },

  // PAGE 8: EDUCATIONAL INSIGHT — THE ISOLATION TRAP (2 of 3)
  page8_educational_insight_2: {
    pageNum: 14,
    type: "informational",
    singleSelect: false,
    heading: "Why talking about it doesn't help — but the right kind does",
    contentByGender: {
      female: {
        image: "/images/placeholder-woman-recovery.webp",
      },
      male: {
        image: "/images/placeholder-man-recovery.webp",
      }
    },
    content: `
Here's what happens to most survivors:

During treatment, everyone shows up. Friends call. Family visits. Meals appear at your door. There's a clear story everyone understands: *"You're fighting cancer."*

Then treatment ends. You ring the bell. And suddenly...

**Silence.**

The people who were there disappear. Not out of malice — they think you're fine now. *You won. You're a survivor.* Time to move on.

But you're not moving on. You're terrified. You're grieving the parts of yourself that changed. And now you're doing it alone.

So you try talking about it. You tell a friend you're anxious about recurrence. They say: *"But you're in remission — try not to think about it!"*

You tell your partner you feel broken. They say: *"You're a survivor — be proud!"*

You tell your family you're struggling. They change the subject.

**After a few attempts at honesty, you stop talking. You isolate.**

And isolation is where fear grows.

The silence becomes louder than the cancer ever was.

**But here's what the research shows:** Survivors who process their experience *with trained guidance* and *with community who understand* recover emotionally **3-5x faster** than those trying to handle it alone.

You don't need people to fix it. You don't need to think positive. You need people who understand what cancer trauma actually feels like. And you need tools designed specifically for recovery from this kind of pain.
    `,
    nextPage: "page9_what_tried"
  },

  // PAGE 9: WHAT HAVE YOU TRIED (Patient-specific)
  page9_what_tried: {
    pageNum: 15,
    type: "question",
    singleSelect: false,
    question: "What have you tried to manage the emotional impact?",
    subtext: "Choose all that apply",
    options: [
      { label: "🛋️ Therapy or counseling (cancer-specific or general)", value: "therapy" },
      { label: "💊 Antidepressants or anti-anxiety medication", value: "medication" },
      { label: "📚 Self-help books or online resources", value: "books" },
      { label: "🧘 Meditation or mindfulness apps", value: "meditation" },
      { label: "👥 Cancer support groups (in-person or online)", value: "support_groups" },
      { label: "📝 Journaling on my own", value: "journaling" },
      { label: "😔 Nothing yet — don't know where to start", value: "nothing" }
    ],
    nextPage: "page10_why_nothing_worked_1"
  },

  // PAGE 10: WHY NOTHING WORKED (1 of 2)
  page10_why_nothing_worked_1: {
    pageNum: 16,
    type: "informational",
    singleSelect: false,
    heading: "Why generic solutions fail cancer survivors",
    image: "/images/placeholder-failed-solutions.webp",
    progressBar: "1 of 2",
    content: `
You've tried things. Maybe several things. And they haven't stuck.

**Here's why:**

**Generic therapy** — A therapist trained in anxiety might help. But they don't understand the specific terror of cancer recurrence. They don't understand why a routine follow-up feels like a threat. They don't get why you can't just think positive.

**Meditation and breathing apps** — They tell you to breathe. To let thoughts pass. But when your brain has learned that danger is real — that your own body betrayed you — meditation can feel like spiritual bypassing. You're not anxious because you're not breathing right. You're anxious because something real happened to you.

**Support groups** — They can make you feel less alone. But many stop at venting. No one leaves with a framework for how to actually recover. And sometimes, hearing everyone's worst-case scenarios re-traumatizes you instead of healing you.

**Medication** — Helpful for some. But medication alone, without processing and nervous system healing, often just numbs the symptom while the underlying trauma stays locked in your body.

**The problem:** None of these address the root issue — **the trauma that's physically encoded in your nervous system.**

What you actually need is **trained guidance** designed specifically for cancer survivors. Something that understands this particular fear. Something that teaches you how to rewire your nervous system. Something that honors your grief while moving you toward genuine healing.
    `,
    nextPage: "page11_why_nothing_worked_2"
  },

  // PAGE 11: WHY NOTHING WORKED (2 of 2)
  page11_why_nothing_worked_2: {
    pageNum: 17,
    type: "informational",
    singleSelect: false,
    heading: "What actually heals cancer-related trauma",
    image: "/images/placeholder-healing-path.webp",
    progressBar: "2 of 2",
    content: `
**Real recovery for cancer survivors requires:**

**1. Permission to prioritize your own healing**

Not as selfish. As essential. You survived. You get to decide what comes next.

**2. Understanding how cancer trauma works**

Knowing that your nervous system responses are *normal* — not a sign you're weak or broken — changes everything. You're not anxious because you're defective. You're anxious because your brain learned that danger is real.

**3. Tools to rewire your nervous system**

Body-based practices that help you move out of survival mode and into presence. Grounding exercises, breathwork, regulated movement. Not meditation that asks you to "just let it go," but actual nervous system recovery.

**4. Space to grieve**

You lost things. Your pre-cancer self. Your sense of invincibility. Your trust in your body. Grieving that isn't dwelling — it's the path to integration. Once you process the loss, you're free to move forward.

**5. Community with people who understand**

Not advice from well-meaning friends. Connection with other survivors who know exactly what this feels like. Who won't minimize it. Who get why a doctor's appointment still triggers panic.

**6. A framework for meaning-making**

The question isn't "Why did this happen?" It's "What do I choose to do with what I've learned?" Survivors who move through recovery with intention heal faster than those who stay frozen.

**This is what recovery looks like** — not pretending the trauma didn't happen, but transforming your relationship with it.
    `,
    nextPage: "page12_intro_oncodiary"
  },

  // PAGE 12: INTRODUCING ONCODIARY (1 of 3)
  page12_intro_oncodiary: {
    pageNum: 18,
    type: "informational",
    singleSelect: false,
    heading: "Introducing OncoDiary — Built specifically for cancer survivors",
    image: "/images/placeholder-oncodiary-products.webp",
    progressBar: "1 of 3",
    content: `
OncoDiary isn't therapy. It's not self-help platitudes. It's a toolkit — designed by a psycho-oncologist with 15+ years of experience — to help you reset your nervous system, process your trauma, and rebuild your life.

Three core tools work together:

📖 "Through The Storm" — The book every survivor needs
A guided journey through the emotional landscape of cancer. Not medical. Not clinical. Real. Written by someone who's helped thousands of survivors navigate this exact path.

📱 OncoDiary App — Daily support in your pocket
Grounding exercises. Guided reflections. Nervous system resets. Real tools, delivered when you need them — at 3am when anxiety hits, or before your next scan.

📝 Wellness Digest — Weekly support and community
Curated insights, reflections, and connection. A monthly reminder that you're not alone.

They work together as a system. The book builds your foundation. The app keeps you grounded day-to-day. The digest connects you to community and new perspectives monthly.
    `,
    nextPage: "page13_how_it_works"
  },

  // PAGE 13: HOW IT WORKS (2 of 3)
  page13_how_it_works: {
    pageNum: 19,
    type: "informational",
    singleSelect: false,
    heading: "How OncoDiary actually works",
    contentByGender: {
      female: {
        image: "/images/placeholder-how-it-works.webp",
      },
      male: {
        image: "/images/placeholder-how-it-works.webp",
      }
    },
    progressBar: "2 of 3",
    content: `
Unlike generic anxiety apps or therapy lite:

STEP 1: ACKNOWLEDGE THE REAL FEAR
You're not broken. Your nervous system learned to be afraid because your body DID get sick. We start by honoring that reality.

STEP 2: RESET YOUR NERVOUS SYSTEM
Using grounding techniques rooted in somatic therapy — proven effective for trauma survivors. Not toxic positivity. Real tools.

STEP 3: PROCESS THE GRIEF
Cancer takes things from you. Your innocence. Your sense of control. Your body's trustworthiness. That grief is valid. We help you move through it, not around it.

STEP 4: BUILD NEW MEANING
As you integrate what you've been through, you discover who you are now. Not in spite of cancer. But including it. That's where real recovery lives.

STEP 5: CONNECT TO COMMUNITY
Survivors heal faster in community. The Wellness Digest connects you monthly with others who understand. Who won't minimize. Who won't tell you to "just move on."

STEP 6: SUSTAIN THE TRANSFORMATION
Recovery isn't a destination. It's a practice. The app and digest become your ongoing toolkit — the same way you wouldn't stop brushing your teeth after one week.
    `,
    nextPage: "page14_what_to_expect"
  },

  // PAGE 14: WHAT TO EXPECT (3 of 3)
  page14_what_to_expect: {
    pageNum: 20,
    type: "informational",
    singleSelect: false,
    heading: "What happens when you actually heal",
    image: "/images/placeholder-transformation.webp",
    progressBar: "3 of 3",
    content: `
<div class="timeline-item">
<span class="timeline-label">FIRST DAY</span>
<span class="timeline-content">You read the opening of "Through The Storm." You recognize yourself on the page. For the first time in months — maybe years — someone gets it. You're not alone.</span>
</div>

<div class="timeline-item">
<span class="timeline-label">WEEK 1</span>
<span class="timeline-content">You start using the grounding exercises from the app. That panic that usually spirals? You have a tool. A way to interrupt it. The panic still comes, but you're not drowning in it anymore.</span>
</div>

<div class="timeline-item">
<span class="timeline-label">WEEK 2-3</span>
<span class="timeline-content">The intrusive thoughts are still there, but they're quieter. Your sleep is a little better. You realize you went an hour without checking your symptoms. You go two hours. Then four.</span>
</div>

<div class="timeline-item">
<span class="timeline-label">WEEK 4+</span>
<span class="timeline-content">The anxiety doesn't disappear, but your relationship with it changes. You're living your life again. Planning things. Saying yes to invitations. Moving like yourself.</span>
</div>

<div class="timeline-item">
<span class="timeline-label">MONTH 2-3</span>
<span class="timeline-content">You've processed some of the grief. You can think about what happened without it consuming you. You're starting to see what you've learned. How you've grown. Not in a toxic way. In a real way.</span>
</div>

<div class="timeline-item">
<span class="timeline-label">MONTH 4+</span>
<span class="timeline-content">You stop organizing your life around fear. You move forward with intention. The cancer is part of your story, but it's not running your story anymore.</span>
</div>

<div class="stats-container">
<div class="stats-header">Recovery isn't linear. There will be hard days. But with the right tools and community, survivors report:</div>
<div class="stat-item">
<span class="stat-check">✓</span>
<span class="stat-text"><strong>70% reduction in intrusive anxiety</strong> within 4 weeks</span>
</div>
<div class="stat-item">
<span class="stat-check">✓</span>
<span class="stat-text"><strong>Sleep returning</strong> within 3-6 weeks</span>
</div>
<div class="stat-item">
<span class="stat-check">✓</span>
<span class="stat-text"><strong>Ability to engage in life again</strong> within 2-3 months</span>
</div>
<div class="stat-item">
<span class="stat-check">✓</span>
<span class="stat-text"><strong>Sustainable peace</strong> within 4-6 months</span>
</div>
</div>
    `,
    nextPage: "page23_heard_from_professional"
  },

  // PAGE 15: RESULTS LOADING
  page15_results_loading: {
    pageNum: 21,
    type: "informational",
    singleSelect: false,
    heading: "Analyzing your recovery profile...",
    loading: true,
    steps: [
      "Analyzing your emotional journey...",
      "Building your personalized recovery plan...",
      "Assessing your support needs...",
      "Creating your action plan..."
    ],
    nextPage: "page16_results_summary"
  },

  // PAGE 16: RESULTS SUMMARY (Gender-aware)
  page16_results_summary: {
    pageNum: 22,
    type: "informational",
    singleSelect: false,
    heading: "Your Emotional Recovery Profile",
    contentByGender: {
      female: {
        image: "/images/placeholder-woman-recovery.webp",
        metrics: {
          nervousSystemRegulation: { level: "dysregulated", bar: 1 },
          emotionalProcessing: { level: "blocked", bar: 1 },
          communitySupport: { level: "isolated", bar: 1 },
          dailyFunctioning: { level: "disrupted", bar: 1 }
        },
        summary: `Your responses indicate you're carrying significant emotional weight from your cancer experience. Your nervous system is still in protection mode. You're isolated. And you're trying to manage this alone.

This is treatable. Thousands of women survivors in your exact position have moved through this phase with the right support.`
      },
      male: {
        image: "/images/placeholder-man-recovery.webp",
        metrics: {
          nervousSystemRegulation: { level: "dysregulated", bar: 1 },
          emotionalProcessing: { level: "blocked", bar: 1 },
          communitySupport: { level: "isolated", bar: 1 },
          dailyFunctioning: { level: "disrupted", bar: 1 }
        },
        summary: `Your responses indicate significant struggle. Your nervous system is hypervigilant. You're isolated (as many men are after cancer). And you're carrying this alone.

Recovery is possible. The men who move through this fastest are those who get support early.`
      }
    },
    nextPage: "page17_conclusions"
  },

  // PAGE 17: CONCLUSIONS FROM YOUR RESULTS
  page17_conclusions: {
    pageNum: 23,
    type: "informational",
    singleSelect: false,
    heading: "What your responses show",
    image: "/images/placeholder-body-nervous-system.webp",
    isPersonalized: true,
    baseContent: `
These aren't separate problems. They're all connected — all rooted in a nervous system that's still in survival mode.

This isn't your fault. And it's not permanent.

When you finally get tools designed specifically for cancer trauma — when your nervous system learns that it's safe — when you process the grief with people who understand — the whole picture changes.

You don't have to feel like this anymore.
    `,
    nextPage: "page18_projection"
  },

  // PAGE 18: PROJECTION
  page18_projection: {
    pageNum: 24,
    type: "question",
    singleSelect: false,
    question: "Once you finally heal, what would change most for you?",
    subtext: "Choose all that apply",
    options: [
      { label: "😴 Sleep through the night without nightmares", value: "sleep" },
      { label: "🆔 Feel like myself again", value: "identity" },
      { label: "👨‍👩‍👧 Reconnect with friends and family", value: "relationships" },
      { label: "🎯 Return to work or life goals", value: "work" },
      { label: "🧠 Stop the intrusive thoughts", value: "thoughts" },
      { label: "☮️ Finally feel at peace", value: "peace" },
      { label: "💚 All of the above", value: "all" }
    ],
    nextPage: "page20_loading"
  },

  // PAGE 20: LOADING
  page20_loading: {
    pageNum: 25,
    type: "informational",
    singleSelect: false,
    heading: "Creating your personalized plan...",
    loading: true,
    steps: [
      "Analyzing your unique needs...",
      "Determining best resources for you...",
      "Building your path forward..."
    ],
    nextPage: "page21_eligibility"
  },

  // PAGE 21: ELIGIBILITY (Patient-specific)
  page21_eligibility: {
    pageNum: 26,
    type: "informational",
    singleSelect: false,
    heading: "You're Eligible for Complete Recovery",
    isResultsPage: true,
    contentByGender: {
      female: {
        message: "Based on your answers, you have what it takes to move through this.\n\nYou've already survived the hardest part. Now healing is possible.\n\nThe women who transform their recovery are those who stop carrying it alone. They find tools designed for what cancer does to your nervous system. They connect with others who actually understand. And they commit to their own healing.\n\nYou've shown incredible strength. Now let's give you the right support to match it.",
        image: "/images/placeholder-woman-complete-recovery.webp"
      },
      male: {
        message: "Based on your answers, you have what it takes to move through this.\n\nYou've already survived the hardest part. Now healing is possible.\n\nThe men who transform their recovery are those who stop carrying it alone. They find tools designed for what cancer does to your nervous system. They connect with others who actually understand. And they commit to their own healing.\n\nYou've shown incredible strength. Now let's give you the right support to match it.",
        image: "/images/placeholder-woman-complete-recovery-man.webp"
      }
    },
    ctaText: "Continue",
    nextPage: "page12_intro_oncodiary"
  },

  // PAGE 23: DID YOU HEAR FROM PROFESSIONAL
  page23_heard_from_professional: {
    pageNum: 28,
    type: "question",
    singleSelect: true,
    question: "Did a psycho-oncologist or oncology team member recommend OncoDiary?",
    emoji: "👂",
    options: [
      { label: "✅ Yes", value: "yes" },
      { label: "❌ No", value: "no" }
    ],
    nextPage: "page24_upsell"
  },

  // PAGE 24: PERSONALIZED UPSELL
  page24_upsell: {
    pageNum: 29,
    type: "upsell",
    singleSelect: false,
    heading: "Personalized Offer"
  }
};

// QUESTION TYPE REFERENCE - PATIENT QUIZ
export const patientQuestionTypes = {
  singleSelect: ["page1_treatment_stage", "page5_treatment_duration", "page6_severity", "page23_heard_from_professional"],
  multiSelect: ["page3_primary_concern", "page4_physical_recovery", "page9_what_tried", "page18_projection"],
  informational: ["home", "page2_social_proof", "page7_educational_insight_1", "page8_educational_insight_2", "page10_why_nothing_worked_1", "page11_why_nothing_worked_2", "page12_intro_oncodiary", "page13_how_it_works", "page14_what_to_expect", "page15_results_loading", "page16_results_summary", "page17_conclusions", "page20_loading", "page21_eligibility"]
};
